import { ENV } from '@/config/env'
import { logger } from '@/utils/logger'

interface HandoffOptions {
  /** The custom-scheme deep link to attempt, e.g. `connexa://qr/abc`. */
  deepLink: string
  /** Whether the user is on iOS (decides between App Store and Play Store). */
  isIOS: boolean
  /** Caller name for logging. */
  ctx: string
}

const FALLBACK_DELAY_MS = 1500

// Android package id of the Connexa app — required to build a Chrome intent
// URL. Kept in sync with connexa-mobile/app.json `android.package`.
const ANDROID_PACKAGE = 'app.getconnexa.connexa'

/**
 * Hand off to the native app via a custom-scheme deep link, with an App/Play
 * Store fallback if the app isn't installed. iOS and Android use different
 * mechanisms because their browsers handle custom schemes differently.
 *
 * iOS Safari: a hidden `<a target="_blank">` click. Safari intercepts the
 * new-window navigation for registered schemes and opens the app without
 * touching the current tab. CRITICAL: this anchor click must fire
 * synchronously inside the original user gesture — any prior `await`
 * (clipboard, network, etc.) consumes the gesture and Safari then silently
 * blocks the `target="_blank"` as a popup, causing the fallback timer to
 * always drop the user to the App Store.
 *
 * Android Chrome: a Chrome `intent://` URL navigated to in the same tab.
 * Chrome launches the app via the package id if installed, or follows
 * `S.browser_fallback_url` to the Play Store if not. Using the iOS trick
 * here would open a blank new tab — Chrome doesn't intercept custom-scheme
 * links the way Safari does, so the new tab navigates to `connexa://...`,
 * Chrome can't render it, and the tab stays white.
 *
 * The proper long-term fix on both platforms is Universal Links / App Links,
 * which neither app supports for these paths yet.
 */
export function handoffToApp({
  deepLink,
  isIOS,
  ctx,
}: HandoffOptions): void {
  const storeUrl = isIOS ? ENV.APP_STORE_URL : ENV.PLAY_STORE_URL
  logger.info(ctx, 'app handoff', { deepLink, isIOS })

  if (isIOS) {
    // Fire the scheme link FIRST, synchronously, inside the user gesture.
    triggerSchemeLinkIOS(deepLink)

    // Clipboard write happens *after* the gesture-sensitive step. A failure
    // here only loses the freshly-installed-app token fallback — the app
    // launch itself already succeeded.
    void writeClipboard(deepLink, ctx)

    // Fallback: if the app isn't installed (or the user dismissed the iOS
    // confirm), the page is still in the foreground after a short delay —
    // navigate Safari to the store. If the app DID open, the page is hidden
    // and we skip the fallback (so we don't disrupt the app launch).
    setTimeout(() => {
      if (!document.hidden) {
        window.location.href = storeUrl
      }
    }, FALLBACK_DELAY_MS)
    return
  }

  // Android: intent:// has a built-in fallback URL, so no setTimeout needed.
  // Same gesture concern applies — set location synchronously first.
  window.location.href = buildAndroidIntentUrl(deepLink, storeUrl)
  void writeClipboard(deepLink, ctx)
}

async function writeClipboard(text: string, ctx: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text)
    logger.debug(ctx, 'clipboard write success')
  } catch (err) {
    logger.warn(ctx, 'clipboard write failed — token will be lost', err)
  }
}

/**
 * Trigger a custom-scheme deep link via a hidden anchor click. The
 * `target="_blank"` makes iOS treat this as a new-window navigation, which
 * it intercepts for registered URL schemes — so the app opens without the
 * current Safari tab being navigated to the invalid scheme URL.
 */
function triggerSchemeLinkIOS(url: string): void {
  const a = document.createElement('a')
  a.href = url
  a.target = '_blank'
  a.rel = 'noopener'
  a.style.position = 'absolute'
  a.style.left = '-9999px'
  document.body.appendChild(a)
  a.click()
  // Give iOS a tick to read the href before we remove the node.
  setTimeout(() => a.remove(), 100)
}

/**
 * Convert a custom-scheme deep link into a Chrome intent URL with a Play
 * Store fallback. Example:
 *   connexa://qr/abc
 *   →
 *   intent://qr/abc#Intent;scheme=connexa;package=...;S.browser_fallback_url=...;end
 */
function buildAndroidIntentUrl(deepLink: string, fallbackUrl: string): string {
  const schemeMatch = /^([a-z][a-z0-9+.-]*):\/\/(.*)$/i.exec(deepLink)
  if (!schemeMatch) {
    // Malformed deep link — best we can do is send the user to the store.
    return fallbackUrl
  }
  const [, scheme, path] = schemeMatch
  return (
    `intent://${path}` +
    `#Intent` +
    `;scheme=${scheme}` +
    `;package=${ANDROID_PACKAGE}` +
    `;S.browser_fallback_url=${encodeURIComponent(fallbackUrl)}` +
    `;end`
  )
}
